/**
 * Retry utility with exponential backoff
 * Attempts to execute an async function with configurable retry logic
 */

/**
 * Sleep for a specified duration
 * @param {number} ms - Duration in milliseconds
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Retry an async function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry configuration
 * @param {number} options.maxAttempts - Maximum number of attempts (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff (default: 2)
 * @param {Function} options.shouldRetry - Function to determine if error is retryable (default: always retry)
 * @param {Function} options.onRetry - Callback called before each retry attempt
 * @returns {Promise<any>} - Result of the function
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    shouldRetry = () => true,
    onRetry = () => {}
  } = options

  let lastError
  let delay = initialDelay

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // If this was the last attempt, throw the error
      if (attempt === maxAttempts) {
        throw error
      }

      // Check if we should retry this error
      if (!shouldRetry(error, attempt)) {
        throw error
      }

      // Call onRetry callback
      onRetry(error, attempt, delay)

      // Wait before retrying
      await sleep(delay)

      // Calculate next delay with exponential backoff
      delay = Math.min(delay * backoffMultiplier, maxDelay)
    }
  }

  // Should never reach here, but TypeScript might complain
  throw lastError
}

/**
 * Common retry strategies for different error types
 */
export const retryStrategies = {
  /**
   * Retry only on network/timeout errors, not on application errors
   */
  networkErrorsOnly: (error) => {
    // Retry on timeout errors
    // - TIMEOUT: our custom timeout code
    // - 57014: PostgreSQL statement timeout
    // - 20: Supabase error code for AbortError
    if (error.code === 'TIMEOUT' || error.code === '57014' || error.code === '20') {
      return true
    }

    // Retry on AbortError (timeout)
    if (error.name === 'AbortError' || (error.details && error.details.includes('AbortError'))) {
      return true
    }

    // Retry on network errors
    if (error.message?.toLowerCase().includes('network')) {
      return true
    }

    // Retry on 500/502/503/504 HTTP errors
    if (error.status && [500, 502, 503, 504].includes(error.status)) {
      return true
    }

    // Don't retry on other errors (validation, auth, etc.)
    return false
  },

  /**
   * Retry on all errors
   */
  alwaysRetry: () => true,

  /**
   * Never retry (useful for testing)
   */
  neverRetry: () => false
}

/**
 * Pre-configured retry for database queries
 */
export async function retryDatabaseQuery(fn, options = {}) {
  return retryWithBackoff(fn, {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
    shouldRetry: retryStrategies.networkErrorsOnly,
    ...options
  })
}

/**
 * Retry strategy for Xero API calls
 * Retries on 500 errors (Internal Server Error) and network failures
 * These are typically transient issues with Edge Function connectivity to Xero
 */
export const xeroApiErrorStrategy = (error, attempt) => {
  // Retry on 500 errors from Xero API (marked as retryable by error sanitizer)
  if (error.status === 500 && error.retryable === true) {
    return true
  }

  // Retry on network errors and timeouts
  if (error.name === 'AbortError' || error.message?.toLowerCase().includes('network')) {
    return true
  }

  // Retry on Xero-specific errors in error message
  if (error.message?.toLowerCase().includes('unable to communicate with xero')) {
    return true
  }

  // Retry on 502/503/504 HTTP errors
  if (error.status && [502, 503, 504].includes(error.status)) {
    return true
  }

  // Don't retry on validation, auth, or other client errors
  return false
}

/**
 * Pre-configured retry for Xero API calls (like create draft)
 */
export async function retryXeroApiCall(fn, options = {}) {
  return retryWithBackoff(fn, {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
    shouldRetry: xeroApiErrorStrategy,
    ...options
  })
}
